import { useEffect, useState } from 'react';
import { PostHogProvider as PHProvider } from '@posthog/react';

/**
 * PostHog, gated on the analytics category of the cookie banner.
 *
 * CookieConsent.jsx offers an analytics toggle and stores the decision under
 * 'meistergruen_cookie_consent'. Loading regardless would make that toggle
 * meaningless, so nothing is fetched until the visitor opts in.
 *
 * Withdrawing consent takes effect on the next page load: a script that has
 * already run cannot be meaningfully unloaded.
 */

const STORAGE_KEY = 'meistergruen_cookie_consent';

/** Dispatched by CookieConsent.jsx whenever a decision is saved. */
export const CONSENT_EVENT = 'meistergruen_cookie_consent_changed';

const options = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
  defaults: '2026-05-30',
  // Our self-hosted instance only serves the unversioned /static/<name>.js
  // path, so skip the default versioned-first lookup (it 302s to /login).
  strict_script_versioning: false,
};

const hasAnalyticsConsent = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    // saveAndClose also accepts a plain string; anything that is not the
    // preferences object counts as no consent.
    return !!parsed?.analytics;
  } catch {
    // localStorage can throw outright (Safari private mode, blocked site data)
    // and a legacy string value fails the parse. Both mean no consent.
    return false;
  }
};

export const PostHogProvider = ({ children }) => {
  const [consented, setConsented] = useState(hasAnalyticsConsent);

  useEffect(() => {
    const update = () => setConsented(hasAnalyticsConsent());
    // The custom event covers a decision made in this tab, 'storage' one made
    // in another. Neither requires a reload.
    window.addEventListener(CONSENT_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(CONSENT_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const apiKey = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
  if (!consented || !apiKey) return <>{children}</>;

  return (
    <PHProvider apiKey={apiKey} options={options}>
      {children}
    </PHProvider>
  );
};

export default PostHogProvider;
